import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useConsumerAuth } from '../../context/ConsumerAuthContext';
import axios from 'axios';
import { CheckCircle, ArrowRight, FileText, Clock, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, loading: authLoading } = useConsumerAuth();
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const applicationId = searchParams.get('application_id');

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/beth-finance/login');
      return;
    }

    if (applicationId && token) {
      confirmPayment();
    } else {
      setLoading(false);
    }
  }, [applicationId, token, authLoading, navigate]);

  const confirmPayment = async () => {
    try {
      await axios.post(`${API}/consumer/applications/${applicationId}/payment-success`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmed(true);
    } catch (err) {
      console.error('Failed to confirm payment:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4"></div>
          <p className="text-gray-600">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#012169] via-[#01337d] to-[#014091] flex flex-col">
      {/* Header */}
      <header className="bg-[#012169]/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/beth-finance" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E31837] rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <span className="text-xl font-bold text-white tracking-tight">BETH Finance</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-[#012169] mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Your credit enhancement application has been submitted successfully.
            </p>

            {applicationId && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">Application ID</p>
                <p className="font-mono font-bold text-[#012169]">{applicationId}</p>
              </div>
            )}

            {/* Submit to Lenders CTA */}
            {applicationId && (
              <Link
                to={`/beth-finance/lenders/${applicationId}`}
                className="block w-full bg-[#012169] hover:bg-[#01337d] text-white py-4 rounded-lg font-semibold text-center mb-6 transition-all"
              >
                Submit to Lenders Now →
              </Link>
            )}

            {/* What's Next */}
            <div className="bg-[#012169]/5 rounded-xl p-6 mb-6 text-left">
              <h3 className="font-bold text-[#012169] mb-4">What happens next?</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0">✓</div>
                  <div>
                    <p className="font-medium text-gray-900">Payment Complete</p>
                    <p className="text-sm text-gray-600">Your credit enhancement is now active</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-[#012169] rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0">2</div>
                  <div>
                    <p className="font-medium text-gray-900">Select Lenders</p>
                    <p className="text-sm text-gray-600">Choose from our network of trusted lenders</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="w-8 h-8 bg-[#012169] rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 flex-shrink-0">3</div>
                  <div>
                    <p className="font-medium text-gray-900">Receive Loan Offers</p>
                    <p className="text-sm text-gray-600">Compare offers and choose the best one for you</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Timeline */}
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 mb-6">
              <Clock className="w-4 h-4" />
              <span>Typical processing time: 24-48 hours</span>
            </div>

            {/* CTA */}
            <Link
              to="/beth-finance/dashboard"
              className="inline-flex items-center justify-center w-full bg-[#E31837] hover:bg-[#c4142f] text-white py-3 rounded-lg font-semibold transition-all"
            >
              Go to Dashboard <ArrowRight className="w-5 h-5 ml-2" />
            </Link>

            {/* Security Note */}
            <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
              <Shield className="w-4 h-4 mr-2 text-green-500" />
              <span>Your payment was processed securely</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
